import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NavLink from './common/NavLink';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box } from '@mui/material';
import RequireVip from './RequireVip'
import { Helmet } from 'react-helmet-async'; 
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme } from './theme';
import { slugify } from '../utils/slugify'; 
import { useCompetitions } from './CompetitionContext';


const CompetitionEloRanking = () => {
    const { competitionId } = 431;
	const { competitions } = useCompetitions();
    const [data, setData] = useState({ competition: { name: 'Liga', groupName: null }, teams: [] });
    const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

    const [sortConfig, setSortConfig] = useState({ key: 'eloRanking', direction: 'desc' });
	const [selectedComp, setSelectedComp] = useState(null); // ← Dodany stan dla selectedComp


    // Pobieranie danych z API
    useEffect(() => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/api/competition-elo/431`)
            .then(response => {
                // Sortowanie danych malejąco po eloRanking
                const sortedTeams = [...response.data.teams].sort((a, b) => b.eloRanking - a.eloRanking);
                setData({ competition: response.data.competition, teams: sortedTeams });
                setLoading(false);
            })
            .catch(error => {
                console.error('Błąd podczas pobierania rankingu Elo:', error);
                setLoading(false);
            });
    }, [competitionId]);
	
	useEffect(() => {
  if (loading) return;

  const selectedComp = 431;

  if (!selectedComp) {
    setError('Liga nie znaleziona.');
    return;
  }
  setSelectedComp(selectedComp); // ← Zapisujemy do stanu

}, [competitionId, competitions, loading]);

    // Funkcja do generowania gradientowego tła dla kolumny Zmiana Elo
    const getEloChangeGradientColor = (value) => {
        if (value === 0) return 'rgba(255, 255, 255, 0.2)'; // Neutralny szary dla 0

        const maxAbs = Math.max(Math.abs(minEloChange), Math.abs(maxEloChange)); // Maksymalna abs wartość dla skalowania
        const scaledValue = Math.abs(value) / maxAbs; // Skalowanie na podstawie abs

        const grayBase = 255;
        let r, g, b, alpha;

        if (value > 0) {
            // Zielony dla pozytywnych
            r = Math.round(grayBase * (1 - scaledValue));
            g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
            b = Math.round(grayBase * (1 - scaledValue));
        } else {
            // Czerwony dla negatywnych
            r = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
            g = Math.round(grayBase * (1 - scaledValue));
            b = Math.round(grayBase * (1 - scaledValue));
        }

        alpha = 0.3 + scaledValue * 0.7; // Minimalna alfa 0.3
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Funkcja sortowania
    const sortData = (key) => {
        let direction = 'desc'; // Domyślnie malejąco dla wszystkich kolumn
        if (key === 'name' && sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc'; // Tylko dla nazwy drużyny pozwalamy na sortowanie rosnące
        }

        setSortConfig({ key, direction });

        const sortedTeams = [...data.teams].sort((a, b) => {
            if (key === 'name') {
                return direction === 'asc' 
                    ? a.name.localeCompare(b.name) 
                    : b.name.localeCompare(a.name);
            }
            return direction === 'asc' 
                ? a[key] - b[key] 
                : b[key] - a[key];
        });
        setData({ ...data, teams: sortedTeams });
    };

    // Dynamiczny nagłówek
    const getHeaderTitle = () => {
        const leagueName = data.competition.name || 'Liga';
        const groupName = data.competition.groupName;
        return groupName && groupName.trim() 
            ? `Ranking Elo - ${leagueName} (${groupName.trim()})`
            : `Ranking Elo - ${leagueName}`;
    };
	
const pageTitle = `${getHeaderTitle()} | SymuLiga`;
const pageDesc = `Aktualny ranking Elo dla ${data.competition.name}. Sprawdź siłę drużyn i zmiany rankingu.`;

    // Obsługa stanu ładowania
if (loading) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                    <Typography variant="h6">Ładowanie danych...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    // Obsługa braku danych
    if (data.teams.length === 0) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                    <Typography variant="h6">Brak danych dla tej ligi.</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    // Obliczenie minimalnej i maksymalnej wartości dla gradientu
    const minEloChange = Math.min(...data.teams.map(team => team.eloChangeSum));
    const maxEloChange = Math.max(...data.teams.map(team => team.eloChangeSum));

    return (
        <ThemeProvider theme={darkTheme}>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDesc} />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDesc} />
			</Helmet>
            <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                <Typography variant="h5" component="h1" sx={{ textAlign: { xs: 'center', sm: 'center' }, padding: '10px 10px', fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
                    {getHeaderTitle()}
                </Typography>
				
					<TableContainer component={Paper} sx={{ width: 'fit-content', margin: '0 auto' }}>
						<Table sx={{ width: 'auto' }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>#</TableCell>
									<TableCell onClick={() => sortData('name')} sx={{ cursor: 'pointer', fontWeight: 'bold', padding: '6px' }}>
										Nazwa drużyny
									</TableCell>
									<TableCell onClick={() => sortData('eloRanking')} sx={{ cursor: 'pointer', fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>
										Elo drużyny
									</TableCell>
									<TableCell onClick={() => sortData('eloChangeSum')} sx={{ cursor: 'pointer', fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>
										Zmiana Elo
									</TableCell>
								</TableRow>
							</TableHead>
								<TableBody>
									{data.teams.map((team, index) => (
										<TableRow key={team.teamId}>
											<TableCell sx={{ textAlign: 'center', padding: '6px' }}>{index + 1}</TableCell>
											<TableCell component={NavLink}
												to={`/team/${team.teamId}/${slugify(team.name)}`}sx={{ padding: '6px' }}>{team.name}
											</TableCell>
											
											<TableCell sx={{ textAlign: 'center', fontWeight: 'bold', padding: '6px' }}>{team.eloRanking}</TableCell>
											<TableCell sx={{
												textAlign: 'center',
												backgroundColor: getEloChangeGradientColor(team.eloChangeSum),
												color: team.eloChangeSum/minEloChange > 0.5 ? 'white' : 'black', // Biały dla mocno negatywnych, czarny dla reszty
												fontWeight: 'bold', // Pogrubienie Zmiana ELO
												padding: '6px', // Zmniejszony padding
												textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)', // Cień dla kontrastu
												transition: 'background-color 0.3s',
												'&:hover': {
													backgroundColor: getEloChangeGradientColor(
														team.eloChangeSum + (team.eloChangeSum > 0 ? 10 : -10)
													),
													transform: 'scale(1.05)',
												},
											}}>
												{team.eloChangeSum}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
						</Table>
					</TableContainer>

            </Box>
        </ThemeProvider>
    );
};

export default CompetitionEloRanking;